/**
 * SCRUM-236 (DS-015 §13.8) — render one `FieldChange` to a { label, detail }
 * pair, fail-closed.
 *
 * Contract (all paths tested, none throws / leaks raw internal values):
 *  - `redacted` → detail = "changed", NO from/to (even if the wire leaks them —
 *     redaction is checked FIRST; gemini pre-plan M3).
 *  - unmapped field (no registry spec, e.g. a leaked `excluded` column) →
 *     label = null, detail = "changed {humanized field}" via react-intl (NOT a
 *     bare string; gemini M1), + a dev-only warning. Never renders a raw value.
 *  - mapped: decode from/to via the field's value formatter and render
 *     created (`from=null`) → the new value; deleted (`to=null`) → "{from} → —";
 *     updated → "{from} → {to}". Structured (`json`) fields render a generic
 *     "updated" marker.
 */
import type { IntlShape } from 'react-intl';
import type { components } from '@artemis/contracts';
import { HISTORY_MESSAGE_IDS as H } from '../../i18n/message-ids.js';
import { lookupFieldFormatter } from './registry.js';
import { formatValue, neutralValue } from './values.js';

type FieldChangeDto = components['schemas']['FieldChangeDto'];

export interface RenderedFieldChange {
  /** Field label — always present (a humanized fallback for unmapped fields), so
   * a consuming `<dl>` always has a `<dt>` before its `<dd>` (valid markup / a11y). */
  readonly label: string;
  /** The rendered change detail (the joined "{from} → {to}" string). */
  readonly detail: string;
  /**
   * The decoded OLD value, rendered SEPARATELY from `toText` so a host can paint
   * an `old → new` delta (e.g. strikethrough on the old value, design parity).
   * `null` when there is no prior value (a set/create) or when the change is
   * redacted / unmapped / structured (then only `detail` is meaningful).
   */
  readonly fromText: string | null;
  /** The decoded NEW value (companion to `fromText`); `null` on a clear/delete
   *  or a redacted / unmapped / structured change. */
  readonly toText: string | null;
}

/** camelCase / snake_case DB key → a humanized fallback label (unmapped fields only). */
function humanizeField(field: string): string {
  const spaced = field
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.length === 0 ? field : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function warnUnmapped(entityType: string, field: string): void {
  if (import.meta.env?.DEV) {
    console.warn(
      `[entity-change] no formatter for ${entityType}.${field} — rendered as a neutral "changed" line. ` +
        `Add it to the registry (the coverage drift test should already be red).`,
    );
  }
}

export function formatFieldChange(
  change: FieldChangeDto,
  entityType: string,
  intl: IntlShape,
  opts?: { currency?: string },
): RenderedFieldChange {
  const spec = lookupFieldFormatter(entityType, change.field);

  // 1. Redacted — presence only, no values, regardless of mapping (DS-015 §5).
  if (change.redacted) {
    const label = spec
      ? intl.formatMessage({ id: spec.labelId, defaultMessage: humanizeField(change.field) })
      : humanizeField(change.field);
    return {
      label,
      detail: intl.formatMessage({ id: H.changeRedacted }),
      fromText: null,
      toText: null,
    };
  }

  // 2. Unmapped — fail-closed: show the (humanized) field with a neutral "changed"
  // and NO value, never a raw value (mirrors backend C1). The humanized key is the
  // <dt> so the <dl> stays valid even on this fail-closed edge.
  if (!spec) {
    warnUnmapped(entityType, change.field);
    return {
      label: humanizeField(change.field),
      detail: intl.formatMessage({ id: H.changeRedacted }),
      fromText: null,
      toText: null,
    };
  }

  const label = intl.formatMessage({
    id: spec.labelId,
    defaultMessage: humanizeField(change.field),
  });

  // 3. Structured value — acknowledge the change generically (display-only).
  if (spec.kind === 'json') {
    return { label, detail: formatValue('json', change.to, intl), fromText: null, toText: null };
  }

  const decode = (value: unknown): string =>
    formatValue(spec.kind, value, intl, {
      currency: opts?.currency,
      enumLabelIds: spec.enumLabelIds,
    });

  const from = change.from as unknown;
  const to = change.to as unknown;
  const hasFrom = from !== null && from !== undefined;
  const hasTo = to !== null && to !== undefined;

  // Decode from/to ONCE and expose them separately (fromText/toText) so a host
  // can render an `old → new` delta with per-side styling; `detail` is the same
  // values joined by the shared connector (kept for the `<dl>` consumer).
  const fromText = hasFrom ? decode(from) : null;
  const toText = hasTo ? decode(to) : null;

  let detail: string;
  if (fromText !== null && toText !== null) {
    detail = intl.formatMessage({ id: H.changeConnector }, { from: fromText, to: toText });
  } else if (fromText === null && toText !== null) {
    detail = toText; // created / set
  } else if (fromText !== null && toText === null) {
    detail = intl.formatMessage(
      { id: H.changeConnector },
      { from: fromText, to: intl.formatMessage({ id: H.valueCleared }) },
    ); // deleted / cleared
  } else {
    detail = neutralValue(intl); // both null on a mapped field — not expected
  }

  return { label, detail, fromText, toText };
}
